import * as React from "react"
import {
  Circle,
  Eye,
  ImagePlus,
  LayoutGrid,
  Maximize2,
  Minus,
  MoreHorizontal,
  SlidersHorizontal,
  Square,
  Trash2,
  Type,
  Undo2,
  ZoomIn,
  ZoomOut,
} from "lucide-react"
import type { FrameKind } from "@/lib/label-canvas/elements"

// The desktop tool rail and the phone dock. Both are plain buttons wired to
// the editor's callbacks; neither knows about the canvas.

export const FRAME_CHOICES: { kind: FrameKind; label: string; Icon: typeof Square }[] = [
  { kind: "rect", label: "Rectangle", Icon: Square },
  { kind: "ellipse", label: "Oval", Icon: Circle },
  { kind: "line", label: "Divider", Icon: Minus },
]

function FrameIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" {...props}>
      <rect x="3.5" y="3.5" width="17" height="17" />
      <ellipse cx="12" cy="12" rx="5" ry="6.5" />
    </svg>
  )
}

/** A pair of laurel sprigs, in the line weight of the other rail icons. */
function LaurelIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M10.5 20.5C5.6 18.9 3.2 14.2 4.4 8.2" />
      <path d="M13.5 20.5c4.9-1.6 7.3-6.3 6.1-12.3" />
      <path d="M4.3 8.4C3 7.5 2.8 5.9 3.6 4.6 4.9 5.5 5.1 7 4.3 8.4Z" />
      <path d="M4.9 12.3c-1.6-.1-2.7-1.2-2.8-2.8 1.6.1 2.7 1.2 2.8 2.8Z" />
      <path d="M6.5 15.9c-1.5.4-2.9-.2-3.6-1.6 1.5-.4 2.9.2 3.6 1.6Z" />
      <path d="M5.2 11.9c.3-1.6 1.5-2.6 3.1-2.6-.3 1.6-1.5 2.6-3.1 2.6Z" />
      <path d="M7.1 15.4c.6-1.4 2-2.1 3.5-1.9-.6 1.5-2 2.2-3.5 1.9Z" />
      <path d="M19.7 8.4c1.3-.9 1.5-2.5.7-3.8-1.3.9-1.5 2.4-.7 3.8Z" />
      <path d="M19.1 12.3c1.6-.1 2.7-1.2 2.8-2.8-1.6.1-2.7 1.2-2.8 2.8Z" />
      <path d="M17.5 15.9c1.5.4 2.9-.2 3.6-1.6-1.5-.4-2.9.2-3.6 1.6Z" />
      <path d="M18.8 11.9c-.3-1.6-1.5-2.6-3.1-2.6.3 1.6 1.5 2.6 3.1 2.6Z" />
      <path d="M16.9 15.4c-.6-1.4-2-2.1-3.5-1.9.6 1.5 2 2.2 3.5 1.9Z" />
    </svg>
  )
}

export function ToolRail({
  onTemplates,
  onText,
  onImage,
  onOrnaments,
  onFrame,
  onUndo,
  canUndo,
  onClear,
  canClear,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomFit,
  active,
}: {
  onTemplates: () => void
  onText: () => void
  onImage: () => void
  onOrnaments: () => void
  onFrame: (kind: FrameKind) => void
  onUndo: () => void
  canUndo: boolean
  onClear: () => void
  canClear: boolean
  zoom: number
  onZoomIn: () => void
  onZoomOut: () => void
  onZoomFit: () => void
  /** The sheet currently open, so its rail button shows as expanded. */
  active: string | null
}) {
  const [framesOpen, setFramesOpen] = React.useState(false)
  return (
    <nav className="studio-rail" aria-label="Label tools">
      <div className="studio-rail-group">
        <div className="studio-rail-heading">Start</div>
        <button
          type="button"
          className="studio-tool"
          aria-haspopup="dialog"
          aria-expanded={active === "templates"}
          onClick={onTemplates}
        >
          <LayoutGrid aria-hidden="true" />
          Templates
        </button>
      </div>

      <div className="studio-rail-group">
        <div className="studio-rail-heading">Add</div>
        <button type="button" className="studio-tool" onClick={onText}>
          <Type aria-hidden="true" />
          Text
        </button>
        <button type="button" className="studio-tool" onClick={onImage}>
          <ImagePlus aria-hidden="true" />
          Image
        </button>
        <button
          type="button"
          className="studio-tool"
          aria-haspopup="dialog"
          aria-expanded={active === "ornaments"}
          onClick={onOrnaments}
        >
          <LaurelIcon />
          Ornaments
        </button>
        <button
          type="button"
          className="studio-tool"
          aria-expanded={framesOpen}
          aria-controls="studio-frame-tools"
          onClick={() => setFramesOpen((open) => !open)}
        >
          <FrameIcon />
          Frame
        </button>
        {framesOpen && (
          <div id="studio-frame-tools" className="studio-subtools">
            {FRAME_CHOICES.map(({ kind, label, Icon }) => (
              <button key={kind} type="button" className="studio-tool" onClick={() => onFrame(kind)}>
                <Icon aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="studio-rail-group">
        <div className="studio-rail-heading">Edit</div>
        <button type="button" className="studio-tool" onClick={onUndo} disabled={!canUndo}>
          <Undo2 aria-hidden="true" />
          Undo
        </button>
        <button type="button" className="studio-tool" onClick={onClear} disabled={!canClear}>
          <Trash2 aria-hidden="true" />
          Clear all
        </button>
      </div>

      <div className="studio-rail-group">
        <div className="studio-rail-heading">View</div>
        <button type="button" className="studio-tool" onClick={onZoomIn} aria-label="Zoom in">
          <ZoomIn aria-hidden="true" />
        </button>
        <span className="studio-zoom-value" aria-live="polite">
          {Math.round(zoom * 100)}%
        </span>
        <button type="button" className="studio-tool" onClick={onZoomOut} aria-label="Zoom out">
          <ZoomOut aria-hidden="true" />
        </button>
        <button type="button" className="studio-tool" onClick={onZoomFit}>
          <Maximize2 aria-hidden="true" />
          Fit
        </button>
      </div>
    </nav>
  )
}

export type DockSheet = "templates" | "text" | "image" | "decor" | "more"

export function MobileDock({
  onText,
  onSheet,
  onUndo,
  canUndo,
  active,
}: {
  onText: () => void
  onSheet: (sheet: DockSheet) => void
  onUndo: () => void
  canUndo: boolean
  active: string | null
}) {
  return (
    <nav className="studio-dock" aria-label="Label tools">
      <button
        type="button"
        className="studio-tool"
        aria-expanded={active === "templates"}
        onClick={() => onSheet("templates")}
      >
        <LayoutGrid aria-hidden="true" />
        Templates
      </button>
      <button type="button" className="studio-tool" onClick={onText}>
        <Type aria-hidden="true" />
        Text
      </button>
      <button type="button" className="studio-tool" aria-expanded={active === "image"} onClick={() => onSheet("image")}>
        <ImagePlus aria-hidden="true" />
        Image
      </button>
      <button type="button" className="studio-tool" aria-expanded={active === "decor"} onClick={() => onSheet("decor")}>
        <LaurelIcon />
        Decor
      </button>
      <button type="button" className="studio-tool" onClick={onUndo} disabled={!canUndo}>
        <Undo2 aria-hidden="true" />
        Undo
      </button>
      <button type="button" className="studio-tool" aria-expanded={active === "more"} onClick={() => onSheet("more")}>
        <MoreHorizontal aria-hidden="true" />
        More
      </button>
    </nav>
  )
}

/** The Decor sheet's two halves on the phone. */
export type DecorSegment = "frames" | "ornaments"

export function DecorSegments({ value, onChange }: { value: DecorSegment; onChange: (next: DecorSegment) => void }) {
  return (
    <div className="studio-seg studio-decor-seg" role="group" aria-label="Frames or ornaments">
      {(
        [
          ["frames", "Frames"],
          ["ornaments", "Ornaments"],
        ] as const
      ).map(([key, label]) => (
        <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}>
          {label}
        </button>
      ))}
    </div>
  )
}

export function MobilePills({
  hasSelection,
  onEdit,
  onPreview,
}: {
  hasSelection: boolean
  onEdit: () => void
  onPreview: () => void
}) {
  return (
    <div className="studio-pills">
      {hasSelection && (
        <button type="button" className="studio-pill studio-pill-ghost" onClick={onEdit}>
          <SlidersHorizontal aria-hidden="true" />
          Edit
        </button>
      )}
      <button type="button" className="studio-pill studio-pill-gold" onClick={onPreview}>
        <Eye aria-hidden="true" />
        Preview
      </button>
    </div>
  )
}
